
import { useParams, useLocation } from "react-router-dom";
import { GroupInviteParams } from "@/types/group";

/**
 * Custom hook to extract group ID from URL parameters,
 * working correctly with both hash and browser routing.
 */
export function useGroupId(): string | null {
  const params = useParams<GroupInviteParams>();
  const location = useLocation();
  
  console.log('useGroupId checking URL:', window.location.href);
  console.log('useGroupId raw params:', params);
  
  // Direct approach with useParams - but only if params.groupId doesn't contain ':groupId'
  if (params.groupId && params.groupId !== ':groupId') {
    console.log('Group ID from params:', params.groupId);
    return params.groupId;
  }
  
  // Get the full URL to ensure we catch all cases
  const fullUrl = window.location.href;
  
  // Extract from raw URL with regex - this will work regardless of routing mechanism
  // Look for invite/ACTUAL-UUID pattern
  const directInviteMatch = fullUrl.match(/\/invite\/([a-zA-Z0-9-]+)(?:\/|$|\?|#)/);
  if (directInviteMatch && directInviteMatch[1] && directInviteMatch[1] !== ':groupId') {
    console.log('Group ID extracted via direct invite URL match:', directInviteMatch[1]);
    return directInviteMatch[1];
  }
  
  // Look for group/ACTUAL-UUID pattern as fallback
  const directGroupMatch = fullUrl.match(/\/group\/([a-zA-Z0-9-]+)(?:\/|$|\?|#)/);
  if (directGroupMatch && directGroupMatch[1] && directGroupMatch[1] !== ':groupId') {
    console.log('Group ID extracted via direct group URL match:', directGroupMatch[1]);
    return directGroupMatch[1];
  }
  
  // Try extracting from hash (for hash router)
  if (location.hash) {
    const hashInviteMatch = location.hash.match(/\/invite\/([a-zA-Z0-9-]+)(?:\/|$|\?|#)/);
    if (hashInviteMatch && hashInviteMatch[1] && hashInviteMatch[1] !== ':groupId') {
      console.log('Group ID extracted from hash invite match:', hashInviteMatch[1]);
      return hashInviteMatch[1];
    }
    
    const hashGroupMatch = location.hash.match(/\/group\/([a-zA-Z0-9-]+)(?:\/|$|\?|#)/);
    if (hashGroupMatch && hashGroupMatch[1] && hashGroupMatch[1] !== ':groupId') {
      console.log('Group ID extracted from hash group match:', hashGroupMatch[1]);
      return hashGroupMatch[1];
    }
  }
  
  // As a last resort, try parsing the pathname
  const pathParts = location.pathname.split('/');
  if (pathParts.length >= 3) {
    const potentialId = pathParts[2];
    if (potentialId && potentialId !== ':groupId' && /^[a-zA-Z0-9-]+$/.test(potentialId)) {
      console.log('Group ID extracted from pathname parts:', potentialId);
      return potentialId;
    }
  }
  
  console.warn('Failed to extract valid group ID from URL using any method');
  return null;
}
